import { API, HOST } from '@/utils/http'
import { toast } from '@/components/ui/toast/use-toast'
import { VIRTUAL_MAP_ID } from './getters'

// Normalize a server map (snake_case) to the client shape the store/getters use.
const toClientMap = (m) => ({
  id: m.uuid,
  map_id: m.map_id,
  name: m.name,
  isDefault: m.is_default,
  color: m.color,
  icon: m.icon,
  basemap: m.basemap,
  shared: m.shared,
  visible: true,
  layerCodes: m.layer_codes || [],
  members_count: m.members_count
})

const mapBase = (orgId) => `${HOST}/org/${orgId}/map`
const orgIdOf = (rootGetters, explicit) => explicit ?? rootGetters['org/getSelected']

// PATCH a map and commit the normalized result.
const patchMap = async ({ commit }, orgId, mapId, data) => {
  const { data: res } = await API({ method: 'PATCH', url: `${mapBase(orgId)}/${mapId}`, data })
  const map = toClientMap(res.map)
  commit('UPDATE_MAP', { orgId, mapId, patch: map })
  return map
}

// Persist a single map's layer ordering/membership.
const persistLayers = (orgId, map) =>
  API({ method: 'PATCH', url: `${mapBase(orgId)}/${map.id}`, data: { layer_codes: map.layerCodes } })

// --- Maps CRUD ---------------------------------------------------------------

export const fetchMaps = async ({ commit }, { orgId }) => {
  const { data } = await API({ method: 'GET', url: mapBase(orgId) })
  const maps = data.map(toClientMap)
  commit('SET_MAPS', { orgId, maps })
  return maps
}

export const createMap = async ({ commit }, { orgId, name, color, icon, basemap, shared }) => {
  try {
    const { data } = await API({
      method: 'POST',
      url: `${mapBase(orgId)}/create`,
      data: { name, color, icon, basemap, shared, layer_codes: [] }
    })
    const map = toClientMap(data.map)
    commit('ADD_MAP', { orgId, map })
    return map
  } catch (error) {
    toast({ title: 'Could not create map', variant: 'destructive' })
    console.error(error)
  }
}

export const renameMap = ({ commit }, { orgId, mapId, name }) =>
  patchMap({ commit }, orgId, mapId, { name }).catch(err => {
    toast({ title: 'Could not rename map', variant: 'destructive' }); console.error(err)
  })

export const setMapStyle = ({ commit }, { orgId, mapId, color, icon, basemap, shared }) => {
  const data = {}
  if (color !== undefined) data.color = color
  if (icon !== undefined) data.icon = icon
  if (basemap !== undefined) data.basemap = basemap
  if (shared !== undefined) data.shared = shared
  return patchMap({ commit }, orgId, mapId, data).catch(err => {
    toast({ title: 'Could not update map', variant: 'destructive' }); console.error(err)
  })
}

export const deleteMap = async ({ commit, getters, dispatch }, { orgId, mapId }) => {
  const target = getters.getMapById(orgId, mapId)
  if (!target || target.isDefault) return
  try {
    // Drop it from the canvas first so no tile references the removed map.
    dispatch('split/hideMap', { orgId, mapId }, { root: true })
    await API({ method: 'DELETE', url: `${mapBase(orgId)}/${mapId}` })
    commit('DELETE_MAP', { orgId, mapId })
    // The deleted map's layers fall back onto the Default Map — persist that.
    const fallback = getters.getDefaultMap(orgId)
    if (fallback) await persistLayers(orgId, fallback)
  } catch (error) {
    toast({ title: 'Could not delete map', variant: 'destructive' })
    console.error(error)
  }
}

// --- Layer ↔ map assignment --------------------------------------------------
// A layer lives on exactly one map. The move endpoint reassigns it atomically:
// it removes the layer from every other map in the org and attaches it here.

const moveLayer = async ({ commit, getters }, { orgId, layerCode, mapId }) => {
  // The Unassigned map is derived, not stored — layers leave it by being claimed by a
  // real map, and nothing can be moved into it.
  if (mapId === VIRTUAL_MAP_ID) return
  const originMap = getters.getMapForLayer(orgId, layerCode)
  if (originMap?.id === mapId) return
  commit('MOVE_LAYER_TO_MAP', { orgId, layerCode, mapId })
  try {
    const target = getters.getMapById(orgId, mapId)
    if (target) {
      await API({ method: 'PUT', url: `${mapBase(orgId)}/${target.id}/layer/${layerCode}` })
    }
  } catch (error) {
    // Roll the optimistic move back to the original owner so the UI doesn't show
    // the layer on the target map while the backend still has it on the source.
    // A virtual origin has no state entry — dropping the code restores it there.
    if (originMap && !originMap.isVirtual) {
      commit('MOVE_LAYER_TO_MAP', { orgId, layerCode, mapId: originMap.id })
    } else {
      commit('DETACH_LAYER', { orgId, layerCode })
    }
    toast({ title: 'Could not move layer', variant: 'destructive' })
    console.error(error)
  }
}

export const moveLayerToMap = (ctx, payload) => moveLayer(ctx, payload)

export const reorderMapLayers = async ({ commit, getters }, { orgId, mapId, layerCodes }) => {
  if (mapId === VIRTUAL_MAP_ID) return
  commit('REORDER_MAP_LAYERS', { orgId, mapId, layerCodes })
  try {
    const map = getters.getMapById(orgId, mapId)
    if (map) await persistLayers(orgId, map)
  } catch (error) {
    toast({ title: 'Could not reorder layers', variant: 'destructive' })
    console.error(error)
  }
}

// --- Map members & invites ---------------------------------------------------

export const fetchMapMembers = async ({ commit, getters }, { orgId, mapId }) => {
  const { data } = await API({ method: 'GET', url: `${mapBase(orgId)}/${mapId}/members` })
  commit('SET_MAP_MEMBERS', { mapId, members: data })
  return getters.getMapMembers(mapId)
}

export const fetchMapInvites = async ({ commit, getters }, { orgId, mapId }) => {
  const { data } = await API({ method: 'GET', url: `${mapBase(orgId)}/${mapId}/invites` })
  commit('SET_MAP_INVITES', { mapId, invites: data })
  return getters.getMapInvites(mapId)
}

export const addMapInvite = async ({ commit, rootGetters }, payload) => {
  const orgId = orgIdOf(rootGetters, payload.orgId)
  const mapId = payload.map_id
  try {
    const { data } = await API({
      method: 'POST',
      url: `${mapBase(orgId)}/${mapId}/invite`,
      data: {
        role_name: payload.role_name,
        email: payload.email,
        phone: payload.phone,
        first_name: payload.first_name,
        last_name: payload.last_name,
        fullname: payload.fullname
      }
    })
    commit('ADD_MAP_INVITE', { mapId, invite: data })
    return data
  } catch (error) {
    toast({ title: 'Could not send invite', variant: 'destructive' })
    console.error(error)
    return null
  }
}

export const updateMapMember = async ({ commit }, { orgId, mapId, mapMemberId, role_name }) => {
  try {
    await API({ method: 'PATCH', url: `${mapBase(orgId)}/${mapId}/member/${mapMemberId}`, data: { role_name } })
    commit('UPDATE_MAP_MEMBER', { mapId, mapMemberId, patch: { role_name } })
    toast({ title: 'Member updated' })
  } catch (error) {
    toast({ title: 'Could not update member', variant: 'destructive' })
    console.error(error)
  }
}

export const deleteMapMember = async ({ commit }, { orgId, mapId, mapMemberId }) => {
  try {
    await API({ method: 'DELETE', url: `${mapBase(orgId)}/${mapId}/member/${mapMemberId}` })
    commit('REMOVE_MAP_MEMBER', { mapId, mapMemberId })
    toast({ title: 'Member removed' })
  } catch (error) {
    toast({ title: 'Could not remove member', variant: 'destructive' })
    console.error(error)
  }
}

export const resendMapInvite = async ({ rootGetters }, { orgId, mapId, uuid }) => {
  const resolvedOrg = orgIdOf(rootGetters, orgId)
  try {
    await API({ method: 'GET', url: `${mapBase(resolvedOrg)}/${mapId}/invite/${uuid}/resend` })
    toast({ title: 'Invite resent' })
  } catch (error) {
    toast({ title: 'Could not resend invite', variant: 'destructive' })
    console.error(error)
  }
}

export const deleteMapInvite = async ({ commit, rootGetters }, { orgId, mapId, uuid }) => {
  const resolvedOrg = orgIdOf(rootGetters, orgId)
  try {
    await API({ method: 'DELETE', url: `${mapBase(resolvedOrg)}/${mapId}/invite/${uuid}` })
    commit('REMOVE_MAP_INVITE', { mapId, uuid })
    toast({ title: 'Invite removed' })
  } catch (error) {
    toast({ title: 'Could not remove invite', variant: 'destructive' })
    console.error(error)
  }
}
